import React, { useEffect, useRef, useState } from "react"
import { observer } from "mobx-react"
import { Box, CircularProgress, Container, useTheme } from "@mui/material"

import { useHistory } from "react-router-dom"
import { useAuthenticityCheckStore } from "api/services/AuthenticityCheck"
import ResponsiveBackground from "../../components/ResponsiveBackground"
import { SearchParameters } from "../../types/BusinessObjects"
import { isUndefined } from "../../utils/util"
import { checkGTIN, convertToHRF, getGeoLocation, isNumeric } from "./util"
import * as applicationIdentifiers from "./ai.json"
import { GTIN_CODE, isMobile } from "utils/Global"
import { globalEndpoint } from "types/Global"
import MappResult from "components/MappResult"
import EanCodeCard from "components/EanCodeCard"
import MappCodeCard from "components/MappCodeCard"

// use this namespace also for subcomponents and for smaller subpages
export const namespace = __filename

const AuthenticityCheck = () => {
  const theme = useTheme()
  const history = useHistory()
  const path = window.location.pathname.split("#")[0]
  const search: SearchParameters =
    window.location.search &&
    window.location.search
      .slice(1)
      .split("&")
      .map((p) => p.split("="))
      .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {})

  const lang = navigator.language.slice(0, 2)
  const [productCode, setProductCode]: any = useState()
  const [latitude, setLatitude] = useState(null)
  const [longitude, setLongitude] = useState(null)
  const [geoLocationStatus, setGeoLocationStatus] = useState("INIT")
  const [endpoint, setEndpoint] = useState(globalEndpoint)
  const loading = useRef(false)

  const authenticityCheckStore = useAuthenticityCheckStore()
  const { loadingAuthenticityCheckList } = authenticityCheckStore

  const getQuerySearchReferrer = (ref) => {
    let queryString = ref.split("?")[1]
    if (queryString) {
      return queryString
    }
    return
  }
  getQuerySearchReferrer(window.document.referrer)

  const searchReferrer =
    getQuerySearchReferrer(window.document.referrer) &&
    getQuerySearchReferrer(window.document.referrer)
      .split("&")
      .map((p) => p.split("="))
      .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {})

  const extractCode = (uid) => {
    if (uid.includes("%5dd2")) {
      let stringBuilder = ""
      const codeArray = convertToHRF(uid, applicationIdentifiers)
      codeArray.forEach((ai) => {
        stringBuilder = stringBuilder + `(${ai.name})${ai.value}`
      })
      uid = stringBuilder
    } else if (uid.includes("%28") && uid.includes("%29")) {
      uid = uid.replaceAll("%28", "(").replaceAll("%29", ")")
    }
    if (checkGTIN(uid)) return uid
    else return null
  }

  useEffect(() => {
    if (
      !sessionStorage.getItem("productCode") &&
      !sessionStorage.getItem("code") &&
      !sessionStorage.getItem("gtin")
    ) {
      history.push("/")
    }
    setProductCode(sessionStorage.getItem("productCode"))
    return () => {
      sessionStorage.removeItem("productCode")
      sessionStorage.removeItem("code")
      sessionStorage.removeItem("gtin")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const code = sessionStorage.getItem("code")
    const gtin = GTIN_CODE // sessionStorage.getItem("gtin")
    const productCode = sessionStorage.getItem("productCode")
    if (searchReferrer) {
      if (
        productCode == null &&
        searchReferrer &&
        searchReferrer["gtin"] &&
        searchReferrer["code"]
      ) {
        setProductCode(`(01)${gtin}(21)${searchReferrer["code"]}`)
        sessionStorage.setItem(
          "productCode",
          `(01)${gtin}(21)${searchReferrer["code"]}`
        )
      } else {
        setProductCode(productCode)
      }
      // @ts-ignore
    } else if (search !== "" || path !== "/" || (code && gtin) || productCode) {
      if (
        productCode == null &&
        !code &&
        search &&
        search["gtin"] &&
        search["code"]
      ) {
        setProductCode(`(01)${gtin}(21)${search["code"]}`)
        sessionStorage.setItem(
          "productCode",
          `(01)${gtin}(21)${search["code"]}`
        )
      }
      if (code && gtin) {
        setProductCode(`(01)${gtin}(21)${code}`)
        sessionStorage.setItem("productCode", `(01)${gtin}(21)${code}`)

        sessionStorage.removeItem("code")
        sessionStorage.removeItem("gtin")
      }
      if (productCode) {
        setProductCode(productCode)
      }
      const codeArray = path.split("/")
      let stringBuilderProduct = ""

      if (codeArray?.length > 0) {
        if (!isUndefined(search.uid)) {
          const searchParameterGTIN = extractCode(search.uid)

          // check for old URL format
          if (searchParameterGTIN) {
            sessionStorage.setItem("productCode", searchParameterGTIN)

            const geoLocation = search.geo?.split("%2C")
            if (geoLocation) {
              setLatitude(
                isNumeric(geoLocation[0]) ? parseFloat(geoLocation[0]) : null
              )
              setLongitude(
                isNumeric(geoLocation[1]) ? parseFloat(geoLocation[1]) : null
              )
              setGeoLocationStatus("TRACKED")
            }

            !isUndefined(search.edp)
              ? setEndpoint(search.edp)
              : setEndpoint(globalEndpoint)
          }
        } else {
          // check for digital link format
          for (let i = 0; i < codeArray.length; i++) {
            if (codeArray[i] === "") continue
            stringBuilderProduct = stringBuilderProduct.concat(
              i % 2 === 0 ? codeArray[i] : `(${codeArray[i]})`
            )
          }
          for (const ai in search) {
            stringBuilderProduct = stringBuilderProduct.concat(
              `(${ai})${search[ai]}`
            )
          }
          if (
            stringBuilderProduct !== "" &&
            !stringBuilderProduct.includes("authenticity")
          ) {
            sessionStorage.setItem("productCode", stringBuilderProduct)
          }
          if (!checkGTIN(stringBuilderProduct)) {
            setGeoLocationStatus("TRACKED")
          }
        }
      }
    }
    return () => {
      sessionStorage.removeItem("productCode")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, search])

  useEffect(() => {
    if (
      !(geoLocationStatus === "TRACKED") &&
      productCode !== null &&
      !search.geo
    ) {
      getGeoLocation()
        .then((geoLocation) => {
          const [latitude, longitude] = geoLocation
          setLatitude(latitude)
          setLongitude(longitude)
          setGeoLocationStatus("TRACKED")
        })
        .catch((err) => {
          console.error("Location failed: " + err)
          setGeoLocationStatus("DENIED")
        })
    }
  }, [productCode, geoLocationStatus, search.geo])

  useEffect(() => {
    if (latitude !== null && longitude !== null) {
      setGeoLocationStatus("TRACKED")
    }
  }, [latitude, longitude, geoLocationStatus])

  useEffect(() => {
    if (
      !loading.current &&
      productCode &&
      (geoLocationStatus === "TRACKED" || geoLocationStatus === "DENIED")
    ) {
      loading.current = true
      authenticityCheckStore
        .fetchAuthenticityCheckList(
          productCode,
          lang,
          latitude,
          longitude,
          endpoint
        )
        .then(() => {
          // window.history.replaceState(null, "", "/#/authenticity")
          sessionStorage.removeItem("productCode")
        })
        .catch((e) => {
          loading.current = false
          console.error(e)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productCode, lang, latitude, longitude, geoLocationStatus])

  // const authenticityResult = React.useMemo(() => {
  //   return authenticityCheckList
  // }, [authenticityCheckList])

  return (
    <ResponsiveBackground theme={theme}>
      <Container
        sx={{
          paddingX: isMobile ? 0 : 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: loadingAuthenticityCheckList ? "64px 0" : "0",
          }}
        >
          {loadingAuthenticityCheckList ||
          (geoLocationStatus === "INIT" && productCode) ? (
            <CircularProgress color={"primary"} size="5vh" />
          ) : (
            <MappResult />
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            height: isMobile ? "auto" : "100%",
            gap: "27px",
            flexDirection: isMobile ? "column" : "row",
            marginTop: "32px",
          }}
        >
          {isMobile && <EanCodeCard />}
          <MappCodeCard />
          {!isMobile && <EanCodeCard />}
        </Box>
      </Container>
    </ResponsiveBackground>
  )
}

export default observer(AuthenticityCheck)
